import type { RefObject } from 'react';
import type { RichTextEditorRef } from '@cotera/client/app/components/app/rte';
import type { Editor } from '@tiptap/react';
import {
  BaseModel,
  createProvidedModelContext,
  MixedSourceWatchable,
  ReadonlyEventWatchable,
  ReadonlyWatchable,
  TargetableModel,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { BroadcastEventType, ResourceType } from '@cotera/api';
import type { BroadcastEventPayloadMap } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { DG } from '@cotera/sdk/core';
import stableStringify from '@cotera/client/app/etc/stringify';
import type {
  DocumentApiData,
  DocumentResourceApiData,
} from './use-document-query';
import {
  toDocumentApiData,
  toDocumentResourceApiData,
} from './use-document-query';
import { WatchablesFactory } from '../watchables/watchables-factory';

function parseEditorState(raw: unknown): DG.DocumentEditor.DocumentState {
  const parsed = raw as DG.DocumentEditor.DocumentState;
  if (
    parsed !== null &&
    typeof parsed === 'object' &&
    'type' in parsed &&
    parsed.type === 'doc'
  ) {
    return parsed;
  }
  return { type: 'doc', content: [] };
}

function extractTitleFromDocState(
  state: DG.DocumentEditor.DocumentState
): string | null {
  const content = state.content;
  if (content === undefined || content.length === 0) {
    return null;
  }
  const first = content[0];
  if (
    first === undefined ||
    first.type !== 'heading' ||
    first.attrs?.['level'] !== 1
  ) {
    return null;
  }
  const textNode = first.content?.[0];
  if (textNode === undefined || textNode.type !== 'text') {
    return null;
  }
  const text = (textNode as { text?: string }).text;
  return text?.trim() ?? null;
}

type DocumentModelProps = {
  documentId: string;
  orgId: string;
  userId: string;
  isNew: boolean;
  mode: 'artifact' | 'page';
  client: Pick<TenantedClient, 'resource'>;
  watchablesFactory: Pick<WatchablesFactory, 'makeResourceUpdatedWatchable'>;
};

export type DocumentResourceState = {
  resource: DocumentResourceApiData | null;
  status: 'loading' | 'ready' | 'not-found';
};

function hasSameSerializedValue(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

/**
 * The editor content is unchanged, but resource metadata (notably `locked`) may
 * have transitioned — e.g. Coco emits `locked: false` on completion even though
 * the final content matches what the editor already holds.
 *
 * Returning `previous` wholesale in that case silently swallows the unlock and
 * leaves the editor stuck read-only until a full page refresh. Instead we take
 * the incoming metadata while preserving the editor's current `editorState`, so
 * the live editor is never disturbed but the lock transition still surfaces.
 *
 * The watchable's `equalityFn` dedupes when nothing actually changed, so a
 * metadata no-op here produces no re-render.
 */
export function applyMetadataOnlyUpdate(
  previous: DocumentResourceState,
  nextResource: DocumentResourceApiData
): DocumentResourceState {
  if (previous.resource === null) {
    return { resource: nextResource, status: 'ready' };
  }
  return {
    status: 'ready',
    resource: {
      ...nextResource,
      document: {
        ...nextResource.document,
        editorState: previous.resource.document.editorState,
      },
    },
  };
}

function shouldAcceptIncomingResourceUpdate(params: {
  hasUnsavedChanges: boolean;
  previousResource: DocumentResourceApiData | null;
  nextResource: DocumentResourceApiData;
}): boolean {
  if (params.hasUnsavedChanges === false) {
    return true;
  }

  const previousLocked = params.previousResource?.document.locked === true;
  const nextLocked = params.nextResource.document.locked === true;

  if (previousLocked || nextLocked) {
    return true;
  }

  return false;
}

function createInitialDocumentResource(
  orgId: string,
  userId: string,
  documentId: string
): DocumentResourceApiData {
  const now = new Date();
  return {
    document: toDocumentApiData({
      id: documentId,
      orgId,
      ownerId: userId,
      editorState: { type: 'doc', content: [] },
      locked: false,
      title: null,
      description: null,
      status: 'active',
      createdAt: now,
      updatedAt: now,
    }),
    meta: null,
  };
}

export class DocumentModel extends BaseModel implements TargetableModel {
  readonly resourceType = 'document' as const;
  private _isTargeted = true;
  readonly titleWatchable: ReadonlyWatchable<string | null>;
  readonly editorStateWatchable: MixedSourceWatchable<DG.DocumentEditor.DocumentState>;
  readonly hasUnsavedChangesWatchable: Watchable<boolean>;
  /**
   * The mounted editor, published for the surfaces that live outside it: the comments
   * rail beside the document and the comment count in the page header. `editorRef`
   * holds the same instance, but a ref cannot tell them when it arrives.
   */
  readonly editorInstanceWatchable: Watchable<Editor | null>;
  readonly resourceWatchable: ReadonlyWatchable<DocumentResourceApiData | null>;
  readonly lockedWatchable: ReadonlyWatchable<boolean>;
  readonly loadStatusWatchable: ReadonlyWatchable<
    'loading' | 'ready' | 'not-found'
  >;
  readonly editorRef: RefObject<RichTextEditorRef | null>;

  readonly documentId: string;
  private readonly _isNewWatchable: Watchable<boolean>;
  readonly isNewWatchable: ReadonlyWatchable<boolean>;
  readonly mode: 'artifact' | 'page';

  private readonly documentDataEventWatchable: ReadonlyEventWatchable<DocumentResourceState>;
  private readonly documentForSaveWatchable: MixedSourceWatchable<DocumentApiData>;

  private readonly _dispose: () => void;

  constructor(props: DocumentModelProps) {
    super(props.documentId);

    const { documentId, client } = props;
    const initialResource = createInitialDocumentResource(
      props.orgId,
      props.userId,
      documentId
    );
    const initialState: DocumentResourceState =
      props.isNew === true
        ? { resource: initialResource, status: 'ready' }
        : { resource: null, status: 'loading' };

    this.documentId = documentId;
    this._isNewWatchable = Watchable.fromValue(props.isNew);
    this.isNewWatchable = this._isNewWatchable;
    this.mode = props.mode;
    this.hasUnsavedChangesWatchable = Watchable.fromValue(false);
    this.editorInstanceWatchable = Watchable.fromValue<Editor | null>(null);
    const watchablesFactory = props.watchablesFactory;
    this.documentDataEventWatchable =
      watchablesFactory.makeResourceUpdatedWatchable<
        DocumentResourceState,
        BroadcastEventPayloadMap[typeof BroadcastEventType.ResourceUpdated]
      >({
        runOnMount: true,
        initialValue: initialState,
        filter: (payload) =>
          payload?.resourceType === 'document' &&
          payload?.resourceId === documentId,
        equalityFn: (a, b) =>
          a.status === b.status &&
          hasSameSerializedValue(a.resource, b.resource),
        fn: async (payload, get) => {
          if (
            payload !== undefined &&
            payload.resourceType === 'document' &&
            payload.document !== undefined
          ) {
            const previous = get(this.documentDataEventWatchable);
            const nextResource: DocumentResourceApiData = {
              document: toDocumentApiData(
                payload.document as Parameters<typeof toDocumentApiData>[0]
              ),
              meta: previous.resource?.meta ?? null,
            };
            if (
              shouldAcceptIncomingResourceUpdate({
                hasUnsavedChanges: get(this.hasUnsavedChangesWatchable),
                previousResource: previous.resource,
                nextResource,
              }) === false
            ) {
              return previous;
            }
            if (
              previous.resource !== null &&
              hasSameSerializedValue(
                get(this.editorStateWatchable),
                parseEditorState(nextResource.document.editorState)
              )
            ) {
              return applyMetadataOnlyUpdate(previous, nextResource);
            }
            return {
              resource: nextResource,
              status: 'ready',
            };
          }
          const result = await client.resource.get({
            type: ResourceType.Document,
            id: documentId,
          });
          if (result.isErr()) {
            const appErr =
              result.error.t === 'application' ? result.error.err : null;
            if (
              appErr !== null &&
              (appErr.errorType === 'NotFound' ||
                appErr.errorType === 'Forbidden')
            ) {
              const previous = get(this.documentDataEventWatchable);
              if (previous.resource !== null) {
                return { resource: previous.resource, status: 'ready' };
              }
              return { resource: null, status: 'not-found' };
            }
            return get(this.documentDataEventWatchable);
          }
          if (result.value.type !== 'document') {
            return get(this.documentDataEventWatchable);
          }
          const next = toDocumentResourceApiData(result.value);
          if (next === null) {
            return get(this.documentDataEventWatchable);
          }
          const previous = get(this.documentDataEventWatchable);
          if (
            shouldAcceptIncomingResourceUpdate({
              hasUnsavedChanges: get(this.hasUnsavedChangesWatchable),
              previousResource: previous.resource,
              nextResource: next,
            }) === false
          ) {
            return previous;
          }
          if (
            previous.resource !== null &&
            hasSameSerializedValue(
              get(this.editorStateWatchable),
              parseEditorState(next.document.editorState)
            )
          ) {
            return applyMetadataOnlyUpdate(previous, next);
          }
          return { resource: next, status: 'ready' };
        },
      });

    this.resourceWatchable = Watchable.from(
      (get) => get(this.documentDataEventWatchable).resource
    );
    this.lockedWatchable = Watchable.from(
      (get) =>
        get(this.documentDataEventWatchable).resource?.document.locked === true
    );
    this.loadStatusWatchable = Watchable.from(
      (get) => get(this.documentDataEventWatchable).status
    );
    this.documentForSaveWatchable = MixedSourceWatchable.from(
      (get) =>
        get(this.documentDataEventWatchable).resource?.document ??
        initialResource.document
    );

    this.editorStateWatchable = MixedSourceWatchable.from((get) =>
      parseEditorState(
        (
          get(this.documentDataEventWatchable).resource?.document ??
          initialResource.document
        ).editorState
      )
    );

    this.editorRef = { current: null };
    const unsubEditorStateSync = this.editorStateWatchable.subscribe(
      (newState) => {
        const editor = this.editorRef.current?.editor;
        if (editor === null || editor === undefined) {
          return;
        }
        const currentJson = editor.getJSON();
        if (hasSameSerializedValue(currentJson, newState)) {
          return;
        }
        editor.commands.setContent(newState, { emitUpdate: false });
      }
    );

    this.titleWatchable = Watchable.from((get) => {
      const state = get(this.editorStateWatchable);
      const fromEditor = extractTitleFromDocState(state);
      if (fromEditor !== null) {
        return fromEditor;
      }
      return (
        get(this.documentDataEventWatchable).resource?.document.title ??
        initialResource.document.title
      );
    });

    this._dispose = () => {
      this.documentForSaveWatchable.unsubscribe();
      unsubEditorStateSync();
      this.documentDataEventWatchable.unsubscribe();
    };
  }

  markUserEdited(): void {
    this.hasUnsavedChangesWatchable.set(true);
  }

  refreshResource(): Promise<void> {
    return this.documentDataEventWatchable.refresh();
  }

  get isNew(): boolean {
    return this._isNewWatchable.snapshot();
  }

  markAsSaved(_editorState: DG.DocumentEditor.DocumentState): void {
    this.hasUnsavedChangesWatchable.set(false);
  }

  /**
   * Call after the first successful save (which creates the server row) so
   * subsequent saves take the {@link update} path with optimistic concurrency
   * instead of repeatedly upserting via {@link create}.
   */
  markNoLongerNew(): void {
    this._isNewWatchable.set(false);
  }

  applySavedDocument(document: DocumentApiData): void {
    this.documentForSaveWatchable.set(document);
  }

  getEditorStateForSave(): DG.DocumentEditor.DocumentState {
    return this.editorStateWatchable.snapshot();
  }

  getDocumentForSave(): DocumentApiData {
    return this.documentForSaveWatchable.snapshot();
  }

  dispose(): void {
    this._dispose();
  }

  get isTargeted(): boolean {
    return this._isTargeted;
  }

  target(): void {
    this._isTargeted = true;
  }

  markAsNotTargeted(): void {
    this._isTargeted = false;
  }

  isEqualTo(other: DocumentModel): boolean {
    return this.id === other.id;
  }
}

const documentModelContext = createProvidedModelContext(
  DocumentModel,
  'DocumentModel not found in scope'
);

/**
 * Provides the document model instance for the current editor tree so
 * `useDocumentModel` binds correctly when several documents share one actions
 * scope. Pass `target` for the active artifact tab only.
 */
export const DocumentModelProvider = documentModelContext.Provider;

export const useDocumentModel = documentModelContext.useModel;
